/**
 * ContactsDirectory — firm-wide contacts directory page.
 *
 * Brief #3 Block 3 contract:
 *   - Server-side search via GET /api/contacts/directory
 *   - Debounce 250ms (via useDebouncedValue hook)
 *   - Pagination: page_size=50 default, cap 200
 *   - Filter pills (exact order): All / Client / Other Side /
 *     Referrer / Third-party / Court / Staff
 *   - Table columns (exact order): Display name, Company, Type,
 *     Email, Phone, Address, Linked matters, Last activity
 *     (BM-H4978, 2 Nov 2026: Address added at Vanessa's request)
 *   - Click row → navigate('/contacts/:id')
 *   - NO silent fallbacks — surface the error if /directory fails;
 *     do not show an empty table as if no contacts exist.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, Users, AlertTriangle, Loader2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import { useDebouncedValue } from "../hooks/useDebouncedValue";

const FILTER_PILLS = [
  { label: "All",         value: null         },
  { label: "Client",      value: "Client"     },
  { label: "Other Side",  value: "Other Side" },
  { label: "Referrer",    value: "Referrer"   },
  { label: "Third-party", value: "Third-party"},
  { label: "Court",       value: "Court"      },
  { label: "Staff",       value: "Staff"      },
];

const PAGE_SIZE = 50;

export default function ContactsDirectory() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 250);
  const [typeFilter, setTypeFilter] = useState(null);
  const [page, setPage] = useState(1);

  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (debouncedQuery && debouncedQuery.trim().length > 0) {
        params.q = debouncedQuery.trim();
      }
      if (typeFilter) {
        params.contact_type = typeFilter;
      }
      const resp = await axios.get(`${API}/contacts/directory`, {
        params,
        withCredentials: true,
      });
      setContacts(Array.isArray(resp.data?.contacts) ? resp.data.contacts : []);
      setTotal(Number(resp.data?.total) || 0);
    } catch (e) {
      // No silent fallback (Brief #3 Block 3). Surface the failure.
      const msg = errorMessage(e, "Failed to load contacts directory");
      setContacts([]);
      setTotal(0);
      setError(msg);
    }
    setLoading(false);
  }, [debouncedQuery, typeFilter, page]);

  // Reset to page 1 when search/filter changes.
  useEffect(() => { setPage(1); }, [debouncedQuery, typeFilter]);

  useEffect(() => { load(); }, [load]);

  const totalPages = useMemo(
    () => (total > 0 ? Math.max(1, Math.ceil(total / PAGE_SIZE)) : 1),
    [total],
  );

  const handleRowClick = (c) => {
    const id = c.id || c.contact_id;
    if (id) navigate(`/contacts/${id}`);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="contacts-directory">
      <header className="mb-6 flex items-center gap-3">
        <Users size={22} className="text-teal" />
        <h1 className="text-2xl font-semibold text-slate-100">
          Contacts directory
        </h1>
      </header>

      {/* Sticky search bar */}
      <div className="sticky top-0 z-10 bg-navy/80 backdrop-blur-sm border border-slate-700/40 rounded-xl p-4 mb-4">
        <div className="relative">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <Input
            data-testid="contacts-directory-search"
            type="text"
            placeholder="Search by name, company, email, phone or address"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Filter pills */}
        <div className="mt-3 flex flex-wrap gap-2">
          {FILTER_PILLS.map((p) => {
            const active = typeFilter === p.value;
            return (
              <button
                key={p.label}
                onClick={() => setTypeFilter(p.value)}
                data-testid={`contacts-directory-filter-${p.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={
                  "px-3 py-1 rounded-full text-xs font-medium border transition-colors " +
                  (active
                    ? "bg-teal text-white border-teal"
                    : "bg-slate-800 text-slate-300 border-slate-700/50 hover:bg-slate-700 hover:text-slate-100")
                }
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div
          data-testid="contacts-directory-error"
          className="mb-4 flex items-start gap-2 p-3 rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-200 text-sm"
        >
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">Contacts directory unavailable</div>
            <div className="text-amber-200/80">{error}</div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-xl border border-slate-700/40 overflow-hidden">
        <table className="w-full text-sm" data-testid="contacts-directory-table">
          <thead className="bg-slate-800/60 text-slate-300">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Display name</th>
              <th className="text-left px-4 py-2 font-medium">Company</th>
              <th className="text-left px-4 py-2 font-medium">Type</th>
              <th className="text-left px-4 py-2 font-medium">Email</th>
              <th className="text-left px-4 py-2 font-medium">Phone</th>
              <th className="text-left px-4 py-2 font-medium">Address</th>
              <th className="text-right px-4 py-2 font-medium">Linked matters</th>
              <th className="text-left px-4 py-2 font-medium">Last activity</th>
            </tr>
          </thead>
          <tbody>
            {loading && contacts.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-slate-400">
                  <Loader2 size={16} className="inline-block animate-spin mr-2" />
                  Loading
                </td>
              </tr>
            ) : !error && contacts.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-4 py-8 text-center text-slate-400"
                  data-testid="contacts-directory-empty"
                >
                  No contacts match your filters.
                </td>
              </tr>
            ) : (
              contacts.map((c) => {
                const id = c.id || c.contact_id || "";
                const company = c.organisation || c.company_name || "";
                const email = c.primary_email || c.email || "";
                const phone = c.primary_phone || c.phone || "";
                const address = (c.address || "").replace(/\s*\n\s*/g, ", ");
                const linkedCount = Array.isArray(c.linked_matters)
                  ? c.linked_matters.length
                  : (typeof c.linked_matters_count === "number"
                    ? c.linked_matters_count
                    : 0);
                const lastActivity = c.last_activity_at || "";
                return (
                  <tr
                    key={id || c.display_name}
                    onClick={() => handleRowClick(c)}
                    data-testid={`contacts-directory-row-${id}`}
                    className="border-t border-slate-700/40 hover:bg-slate-800/40 cursor-pointer"
                  >
                    <td className="px-4 py-2 text-slate-100">{c.display_name || ""}</td>
                    <td className="px-4 py-2 text-slate-300">{company}</td>
                    <td className="px-4 py-2 text-slate-300">{c.contact_type || ""}</td>
                    <td className="px-4 py-2 text-slate-300">{email}</td>
                    <td className="px-4 py-2 text-slate-300">{phone}</td>
                    <td
                      className="px-4 py-2 text-slate-300 max-w-[18rem] truncate"
                      title={address}
                      data-testid={`contacts-directory-address-${id}`}
                    >
                      {address}
                    </td>
                    <td className="px-4 py-2 text-right text-slate-300">{linkedCount}</td>
                    <td className="px-4 py-2 text-slate-300">
                      {lastActivity ? lastActivity.slice(0, 10) : "\u2014"}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      <div className="mt-4 flex items-center justify-between text-sm text-slate-400">
        <div data-testid="contacts-directory-total">
          {total} contact{total === 1 ? "" : "s"}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            data-testid="contacts-directory-prev-page"
            className="border-slate-700 text-slate-300"
          >
            Previous
          </Button>
          <span data-testid="contacts-directory-page-indicator">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            data-testid="contacts-directory-next-page"
            className="border-slate-700 text-slate-300"
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
